import { ContractViewModel } from '@ail/contracts-api'
import { DocumentKind, DocumentViewModel } from '@ail/documents-api'
import { OrderViewModel } from '@ail/orders-api'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Arr } from '../../../helpers/Arr'
import { printDate } from '../../../helpers/Utils'
import { useAuth } from '../../../hooks/useAuth'
import './DocumentDetails.scss'
import { Loader } from '../../common/Loader/Loader'

export function DocumentDetails(props: {
  contract: ContractViewModel
  documents: DocumentViewModel[]
  request?: OrderViewModel
  onDocumentDownload: (documentId: string, filename: string) => void
  onRequestDownload: (request: OrderViewModel) => void
}) {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterByYear, setFilterByYear] = useState(new Date().getFullYear() + '')
  const [filterAutoChange, setFilterAutoChange] = useState(false)
  const [hideFilterLabels, setHideFilterLabels] = useState(false)
  const [documentKinds, setDocumentKinds] = useState([
    {
      key: 'Contract',
      icon: 'icon-contract',
      visible: true,
      tr: 'CONTRACT',
    },
    {
      key: 'Cost',
      icon: 'icon-doc-cost',
      visible: true,
      tr: 'COST',
    },
    {
      key: 'Fuel',
      icon: 'icon-doc-fuel',
      visible: true,
      tr: 'FUEL',
    },
    {
      key: 'OutgoingInvoice',
      icon: 'icon-doc-invoice',
      visible: true,
      tr: 'INVOICE',
    },
    {
      key: 'Purchase',
      icon: 'icon-doc-purchase',
      visible: true,
      tr: 'PURCHASE',
    },
    {
      key: 'Registration',
      icon: 'icon-doc-registration',
      visible: true,
      tr: 'REGISTRATION',
    },
    {
      key: 'Settings',
      icon: 'icon-doc-settings',
      visible: true,
      tr: 'SETTINGS',
    },
    {
      key: 'CustomerManagedInvoice',
      addKeys: ['CustomerManagedContract'],
      icon: 'icon-doc-folder',
      addIcon: 'icon-doc',
      visible: true,
      tr: 'OTHERS',
    },
  ])

  const filtersContainer = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const { t } = useTranslation('common')

  const groupedDocs = useMemo(() => {
    const sortedDocuments = Arr.sort(props.documents, (d) => (d.timestamp ? new Date(d.timestamp) : undefined), 'DESC')

    if (props.contract.requestId && props.request) {
      sortedDocuments.push({
        id: props.request.id,
        timestamp: props.request.modified,
        kind: DocumentKind.Contract,
        description: 'APPROVED_VEHICLE_ORDER',
        fileName: '',
      })
    } else if (props.contract.requestId && !props.request && user?.isAdmin) {
      sortedDocuments.push({
        id: props.contract.requestId,
        timestamp: '',
        kind: DocumentKind.Contract,
        description: 'APPROVED_VEHICLE_ORDER_ERROR',
        fileName: '',
      })
    }
    const groups = documentKinds.map((kind) => {
      const docs = sortedDocuments.filter((d) => d.kind === kind.key || kind.addKeys?.includes(d.kind))
      return {
        kind: kind,
        docs,
      }
    })

    return groups.filter((g) => g.docs.length)
  }, [props.documents, props.contract.requestId, props.request, documentKinds, user])

  const kindIconMap = useMemo(() => {
    const map = new Map<string, string>()
    documentKinds.forEach((kind) => {
      map.set(kind.key, kind.addIcon ? kind.addIcon : kind.icon)
    })
    return map
  }, [documentKinds])

  const uniqueYears = useMemo(() => {
    const result: string[] = []
    for (const kind of groupedDocs) {
      for (const doc of kind.docs) {
        const year = doc.timestamp ? new Date(doc.timestamp).getFullYear().toString() : undefined
        if (year && !result.includes(year)) {
          result.push(year)
        }
      }
    }
    return result.toSorted((a, b) => b.localeCompare(a))
  }, [groupedDocs])

  const visibleKinds = useMemo(() => {
    const kinds: string[] = []
    documentKinds
      .filter((dk) => dk.visible)
      .map((dk) => {
        kinds.push(dk.key)
        if (Arr.any(dk.addKeys)) {
          dk.addKeys.forEach((q) => kinds.push(q))
        }
      })
    return kinds
  }, [documentKinds])

  const filteredDocuments = useMemo(() => {
    const documents = !groupedDocs.length
      ? []
      : groupedDocs.flatMap((group) =>
          group.docs.filter((d) => {
            let valid = false
            // Filters by document kind and year.
            if (visibleKinds.includes(d.kind) && !!filterByYear && !isNaN(Number(filterByYear))) {
              valid = !d.timestamp || new Date(d.timestamp).getFullYear().toString() === filterByYear
            } else {
              valid = visibleKinds.includes(d.kind)
            }

            // Filters by keyword
            if (searchKeyword !== '' && valid) {
              const description = d.description
                ? props.contract.requestId === d.id
                  ? t(d.description)
                  : d.description
                : ''
              const invoiceId = d.OutgoingInvoiceId || d.IncomingCostStateId

              valid = description.toLowerCase().includes(searchKeyword.toLowerCase())
              valid = valid || (!!d.timestamp && printDate(d.timestamp).includes(searchKeyword))
              valid = valid || (!!invoiceId && invoiceId.toString().includes(searchKeyword))
            }

            return valid
          })
        )
    if (groupedDocs.length > 0 && documents.length === 0) {
      setFilterByYear('All')
      setFilterAutoChange(true)
    }
    return documents
  }, [groupedDocs, visibleKinds, filterByYear, searchKeyword, props.contract.requestId, t])

  useEffect(() => {
    function updateHideFilterLabels() {
      const filtersWidth = filtersContainer.current?.offsetWidth ?? 0
      setHideFilterLabels(filtersWidth < 450)
    }
    window.addEventListener('resize', updateHideFilterLabels)
    return () => window.removeEventListener('resize', updateHideFilterLabels)
  }, [])

  const toggleDocumentKind = (kindKey: string) => {
    setDocumentKinds((prevKinds) =>
      prevKinds.map((kind) => (kind.key === kindKey ? { ...kind, visible: !kind.visible } : kind))
    )
  }

  const filterByDate = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterByYear(e.target.value)
    if (filterAutoChange === true) {
      setFilterAutoChange(false)
    }
  }

  const downloadColumnTemplate = (rowData: DocumentViewModel) => {
    return (
      <i
        className="icon-download"
        onClick={() =>
          rowData.id === props.contract.requestId && props.request
            ? props.onRequestDownload(props.request)
            : props.onDocumentDownload(rowData.id, rowData.fileName)
        }
      />
    )
  }

  function uploadDateColumnTemplate(rowData: DocumentViewModel) {
    return <>{!!rowData.timestamp && hightlight(searchKeyword, printDate(rowData.timestamp))}</>
  }

  function hightlight(keyword: string, text: string) {
    const index = text.toLowerCase().indexOf(keyword.toLowerCase())

    if (index !== -1) {
      return (
        <>
          {text.substring(0, index)}
          <span style={{ backgroundColor: 'yellow' }}>{text.substring(index, index + keyword.length)}</span>
          {text.substring(index + keyword.length)}
        </>
      )
    } else {
      return text
    }
  }

  function descriptionColumnTemplate(rowData: DocumentViewModel, icon?: string) {
    const { OutgoingInvoiceId, IncomingCostStateId } = rowData
    const iconToDisplay = icon || 'icon-doc'
    const description = rowData.description
      ? props.contract.requestId === rowData.id
        ? t(rowData.description)
        : rowData.description
      : ''
    const highlightedDescription = hightlight(searchKeyword, description)
    const invoiceId = OutgoingInvoiceId || IncomingCostStateId
    const error = rowData.description && rowData.description === 'APPROVED_VEHICLE_ORDER_ERROR'
    return (
      <>
        <div className="flex">
          <i className={rowData?.id === props.contract.requestId ? 'icon-order' : iconToDisplay} />
          <span className="documentsDetails">
            <span title={description}>{highlightedDescription}</span>
            {error && user?.isAdmin && rowData.id && (
              <span style={{ marginLeft: '50px' }} title={rowData.id}>
                Request ID: {rowData.id}
              </span>
            )}

            {(rowData.kind === 'OutgoingInvoice' || rowData.kind === 'Cost') &&
              (Number.isInteger(OutgoingInvoiceId) || Number.isInteger(IncomingCostStateId)) &&
              invoiceId !== undefined &&
              invoiceId !== null && (
                <span className="invoice-id">
                  {t('INVOICE_ID')} {hightlight(searchKeyword, invoiceId?.toString())}
                </span>
              )}
          </span>
        </div>
      </>
    )
  }

  return (
    <Loader blocking={false}>
      {groupedDocs.length > 0 && (
        <>
          <div
            ref={filtersContainer}
            className="detailTitle docTitleArea relative"
            style={{ flexWrap: 'wrap', paddingRight: '5px' }}
          >
            <div className="titleDoc">
              <span>
                <i className="icon-doc-folder" />
                {t('DOCUMENTS')}
              </span>
              <div className="toolArea">
                {
                  <div className="input-area">
                    <input
                      value={searchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      type="search"
                      className="transparent"
                    ></input>
                    {!!searchKeyword && <i className="icon-clear" onClick={() => setSearchKeyword('')} />}
                    <i className="icon-search" />
                  </div>
                }
              </div>
            </div>
          </div>
          <div className="toggle-grouping-wrap">
            <div className="selectArea">
              <select
                onChange={(e) => filterByDate(e)}
                className="filterByYear"
                style={filterAutoChange ? { backgroundColor: 'yellow' } : undefined}
              >
                <option className="filterByYearOptions">{t('ALL')}</option>
                {uniqueYears.map((obj, i) =>
                  obj === filterByYear ? (
                    <option key={i} className="filterByYearOptions" selected={true}>
                      {obj}
                    </option>
                  ) : (
                    <option key={i} className="filterByYearOptions">
                      {obj}
                    </option>
                  )
                )}
              </select>
              <i className="icon-bottom" />
            </div>

            <div className="groups-toggle">
              {groupedDocs.map((group, i) => (
                <div
                  key={i}
                  title={hideFilterLabels ? t(group.kind.tr) : undefined}
                  className="group-toggle-row"
                  onClick={() => toggleDocumentKind(group.kind.key)}
                >
                  {' '}
                  <i className={group.kind.visible ? 'icon-check active' : 'icon-check inactive'} />
                  <i className={`${group.kind.icon}`} />
                  <label>{hideFilterLabels ? '' : t(group.kind.tr)}</label>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="details docDetails documents">
        <div className="docs-wrap">
          <DataTable value={filteredDocuments} emptyMessage={t('NO_DOCUMENTS_FOUND')}>
            <Column
              className="description-column"
              header={t('DESCRIPTION')}
              body={(row: DocumentViewModel) => descriptionColumnTemplate(row, kindIconMap.get(row.kind))}
            />
            <Column header={t('UPLOAD_DATE')} body={uploadDateColumnTemplate} />
            <Column className="download-column" body={downloadColumnTemplate} />
          </DataTable>
        </div>
      </div>
    </Loader>
  )
}
